/**
 * The arithmetic behind the gallery's zoom.
 *
 * Pure and separate from the overlay so the one decision that matters — how far
 * this particular photograph can be magnified before it stops showing anything
 * new — can be reasoned about and tested without a DOM.
 */

/**
 * Below this the second click is not worth offering: the image barely moves and
 * a control that does almost nothing reads as broken.
 */
const WORTH_A_CLICK = 1.1;

/**
 * How far a photograph can be magnified before it runs out of pixels.
 *
 * "Native" here means 1:1 in CSS pixels, which is what the old site's loupe
 * showed and what the redesign matches. Capping by DEVICE pixels instead would
 * put the limit below 1 on a retina screen — no zoom at all — because the
 * catalogue's 1200px uploads are already close to the panel's device width.
 */
export function zoomScale(intrinsicWidth: number, fittedWidth: number): number {
  if (!Number.isFinite(intrinsicWidth) || !Number.isFinite(fittedWidth)) return 1;
  if (intrinsicWidth <= 0 || fittedWidth <= 0) return 1;
  return Math.max(1, intrinsicWidth / fittedWidth);
}

export function hasZoomHeadroom(scale: number): boolean {
  return scale >= WORTH_A_CLICK;
}

/**
 * Keeps a panned image covering its frame: it may not travel so far that its
 * own edge pulls inside, which would show dead space beside the photograph.
 * An image smaller than the frame has nowhere to go and pins at zero.
 */
/**
 * Where the magnified image sits when the zoom simply follows the pointer.
 *
 * `ratio` is the pointer's position across the panel, 0 at one edge and 1 at the
 * other, and it maps linearly onto the image's full travel — so sweeping the
 * panel sweeps the whole photograph past the window, evenly. This is what the
 * old site's loupe does.
 *
 * Deliberately not `focalPan`, which centres the aimed-at point: centring
 * clamps before the extremes, so the far edge of a long rifle could not be
 * reached without the pointer leaving the panel entirely.
 */
export function trackPan(ratio: number, imageSize: number, frameSize: number): number {
  const travel = Math.max(0, imageSize - frameSize);
  const held = Math.min(1, Math.max(0, ratio));
  const offset = -held * travel;
  return offset === 0 ? 0 : offset;
}

export interface PaintedBox {
  width: number;
  height: number;
  left: number;
  top: number;
}

/**
 * Where `object-contain` actually paints the photograph inside its element.
 *
 * The element fills the panel; the photograph is letterboxed inside it and
 * centred. A rifle is 1200x300 in a 653x392 panel, so more than half that panel
 * is empty — aiming a zoom at the element rather than at the photograph puts the
 * focal point in the margin.
 */
export function paintedBox(
  frameWidth: number,
  frameHeight: number,
  naturalWidth: number,
  naturalHeight: number,
): PaintedBox {
  const empty = { width: 0, height: 0, left: 0, top: 0 };
  if (!(naturalWidth > 0) || !(naturalHeight > 0)) return empty;
  if (!(frameWidth > 0) || !(frameHeight > 0)) return empty;

  const scale = Math.min(frameWidth / naturalWidth, frameHeight / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  return {
    width,
    height,
    left: (frameWidth - width) / 2,
    top: (frameHeight - height) / 2,
  };
}

/**
 * Where the magnified image should sit, given the point the reader aimed at.
 *
 * `ratio` is 0–1 across the image: the middle for the toolbar button, the
 * pointer's position when they clicked the photograph itself. The aimed-at point
 * ends up in the centre of the frame, unless that would pull an edge inside it.
 *
 * Scaling from the corner and leaving the image there is the obvious
 * implementation and the wrong one — on a catalogue shot with white margins it
 * lands on a blank corner and looks broken.
 */
export function focalPan(ratio: number, scaledSize: number, frameSize: number): number {
  return clampPan(frameSize / 2 - ratio * scaledSize, scaledSize, frameSize);
}

export function clampPan(offset: number, imageSize: number, frameSize: number): number {
  const travel = Math.max(0, imageSize - frameSize);
  const clamped = Math.min(0, Math.max(-travel, offset));
  // Normalised because this goes straight into a transform: `translate(-0px)`
  // is valid but reads as a bug to anyone inspecting the element.
  return clamped === 0 ? 0 : clamped;
}
